import React, { useEffect, useRef } from 'react';
import { Check, X } from 'lucide-react';
import { type StripePlan } from '@/lib/stripe-config';
import { cn } from '@/lib/utils';
import { trackFeaturesViewed } from '@/lib/analytics';

interface FeaturesComparisonProps {
  currentPlan?: StripePlan | null;
}

// Feature categories following Notion/Slack pattern
interface Feature {
  category: string;
  name: string;
  starter: string | boolean;
  plus: string | boolean;
  pro: string | boolean;
}

const FEATURES: Feature[] = [
  // Search & Booking
  {
    category: 'Search & Booking',
    name: 'Search flights worldwide',
    starter: true,
    plus: true,
    pro: true,
  },
  {
    category: 'Search & Booking',
    name: 'Find the best hotel deals',
    starter: true,
    plus: true,
    pro: true,
  },
  {
    category: 'Search & Booking',
    name: 'Unlimited searches',
    starter: true,
    plus: true,
    pro: true,
  },
  {
    category: 'Search & Booking',
    name: 'AI-powered travel planning',
    starter: true,
    plus: true,
    pro: true,
  },
  
  // Savings & Rewards
  {
    category: 'Savings & Rewards',
    name: 'Save more on hotel bookings',
    starter: true,
    plus: true,
    pro: true,
  },

  
  // Support
  {
    category: 'Support',
    name: 'Get help when you need it',
    starter: true,
    plus: true,
    pro: true,
  },
];

const PLAN_ORDER: StripePlan[] = ['VOY_PRO', 'VOY_BUSINESS', 'VOY_FAMILY'];
const PLAN_NAMES: Record<StripePlan, string> = {
  VOY_FREE: 'Free',
  VOY_PRO: 'Pro',
  VOY_BUSINESS: 'Business',
  VOY_FAMILY: 'Family',
};
const PLAN_FEATURE_KEY: Record<StripePlan, 'starter' | 'plus' | 'pro'> = {
  VOY_FREE: 'starter',
  VOY_PRO: 'starter',
  VOY_BUSINESS: 'plus',
  VOY_FAMILY: 'pro',
};

export function FeaturesComparison({ currentPlan }: FeaturesComparisonProps) {
  const sectionRef = useRef<HTMLElement>(null);
  const hasTrackedRef = useRef(false);

  // Track features view when section becomes visible in viewport
  useEffect(() => {
    const section = sectionRef.current;
    if (!section || hasTrackedRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasTrackedRef.current) {
            trackFeaturesViewed();
            hasTrackedRef.current = true;
            observer.disconnect();
          }
        });
      },
      {
        // Trigger when at least 50% of the section is visible
        threshold: 0.5,
      }
    );

    observer.observe(section);

    return () => {
      observer.disconnect();
    };
  }, []);
  
  // Group features by category
  const categories = Array.from(new Set(FEATURES.map(f => f.category)));
  
  return (
    <section ref={sectionRef} className="max-w-7xl mx-auto px-4 mb-12 md:mb-16">
      <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-center mb-8 md:mb-12 text-white drop-shadow-lg">
        Everything you need to travel smarter
      </h2>
      
      {/* Desktop Table View */}
      <div className="hidden md:block overflow-hidden rounded-3xl bg-white/[0.08] dark:bg-zinc-800/50 backdrop-blur-xl border border-white/30 dark:border-zinc-700/50 shadow-[0_8px_24px_rgba(0,0,0,0.15)] dark:shadow-xl">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b-2 border-gray-200/50 bg-white/50">
                <th className="text-left py-6 px-6 font-semibold text-[#0F172A] text-base">
                  Features
                </th>
                {PLAN_ORDER.map((plan) => (
                  <th
                    key={plan}
                    className={cn(
                      'text-center py-6 px-6 font-semibold text-base',
                      currentPlan === plan
                        ? 'bg-[#5B6FED]/10 text-[#5B6FED]'
                        : 'text-[#0F172A]'
                    )}
                  >
                    {PLAN_NAMES[plan]}
                    {currentPlan === plan && (
                      <span className="block text-xs font-normal text-green-600 mt-1">
                        (Your Plan)
                      </span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {categories.map((category) => {
                const categoryFeatures = FEATURES.filter(f => f.category === category);
                return (
                  <React.Fragment key={category}>
                    {/* Category Header */}
                    <tr className="bg-white/5 border-b border-white/10">
                      <td
                        colSpan={4}
                        className="py-4 px-6 font-semibold text-sm uppercase text-white/80 tracking-wider"
                      >
                        {category === 'Search & Booking' && '✈️ '}
                        {category === 'Savings & Rewards' && '💰 '}
                        {category === 'Travel Features' && '👥 '}
                        {category === 'Support' && '🛟 '}
                        {category}
                      </td>
                    </tr>
                    {/* Features in this category */}
                    {categoryFeatures.map((feature, featIdx) => (
                      <tr
                        key={`${category}-${featIdx}`}
                        className={cn(
                          'border-b border-gray-100/50 transition-colors hover:bg-white/30',
                          featIdx === categoryFeatures.length - 1 && 'border-b-2 border-gray-200'
                        )}
                      >
                        <td className="py-5 px-6 text-white font-medium">
                          {feature.name}
                        </td>
                        {PLAN_ORDER.map((plan) => {
                          const planKey = PLAN_FEATURE_KEY[plan];
                          const value = feature[planKey];
                          return (
                            <td
                              key={plan}
                              className={cn(
                                'text-center py-5 px-6',
                                currentPlan === plan && 'bg-[#5B6FED]/5'
                              )}
                            >
                              {typeof value === 'boolean' ? (
                                value ? (
                                  <Check className="w-5 h-5 text-[#5B6FED] mx-auto" />
                                ) : (
                                  <X className="w-5 h-5 text-gray-300 mx-auto" />
                                )
                              ) : (
                                <span className="text-white font-medium">{value}</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile Accordion View */}
      <div className="md:hidden space-y-4">
        {PLAN_ORDER.map((plan) => (
          <div
            key={plan}
            className="rounded-3xl bg-white/[0.08] dark:bg-zinc-800/50 backdrop-blur-xl border border-white/30 dark:border-zinc-700/50 shadow-[0_8px_24px_rgba(0,0,0,0.15)] dark:shadow-xl overflow-hidden"
          >
            <div className={cn(
              'p-5 md:p-6 border-b border-gray-200/50',
              currentPlan === plan && 'bg-[#5B6FED]/10'
            )}>
              <h3 className="text-lg md:text-xl font-bold text-white text-center">
                {PLAN_NAMES[plan]}
                {currentPlan === plan && (
                  <span className="block text-xs md:text-sm font-normal text-green-400 mt-1">
                    (Your Plan)
                  </span>
                )}
              </h3>
            </div>
            <div className="p-5 md:p-6 space-y-5 md:space-y-6">
              {categories.map((category) => {
                const categoryFeatures = FEATURES.filter(f => f.category === category);
                return (
                  <div key={category} className="space-y-3">
                    <h4 className="text-sm font-semibold uppercase text-white/80 tracking-wider">
                      {category === 'Search & Booking' && '✈️ '}
                      {category === 'Savings & Rewards' && '💰 '}
                      {category === 'Travel Features' && '👥 '}
                      {category === 'Support' && '🛟 '}
                      {category}
                    </h4>
                    {categoryFeatures.map((feature, idx) => {
                      const planKey = plan.toLowerCase().replace('voy_', '') as 'starter' | 'plus' | 'pro';
                      const value = feature[planKey];
                      return (
                        <div
                          key={idx}
                          className="flex items-center justify-between py-2.5 md:py-2 border-b border-gray-100/50 last:border-0 min-h-[44px]"
                        >
                          <span className="text-white text-sm md:text-base pr-2">{feature.name}</span>
                          {typeof value === 'boolean' ? (
                            value ? (
                              <Check className="w-5 h-5 md:w-6 md:h-6 text-[#5B6FED] flex-shrink-0" />
                            ) : (
                              <X className="w-5 h-5 md:w-6 md:h-6 text-gray-300 flex-shrink-0" />
                            )
                          ) : (
                            <span className="text-white font-medium text-sm md:text-base">{value}</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

